import React from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import CITIES from '../../data/cities.json';

const StepLivraison = ({
    customerInfo,
    setCustomerInfo,
    selectedCity,
    setSelectedCity,
    selectedDelivery,
    setSelectedDelivery,
    cityData,
    canProceed,
    onContinue,
}) => {
    return (
        <motion.div
            key="step-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            {/* Customer info */}
            <div className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-4">
                <h2 className="font-bold text-lg flex items-center gap-2">
                    <Icon icon="solar:user-bold-duotone" className="text-lime-500 text-xl" />
                    Informations
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs text-slate-500 dark:text-store-gray mb-1 block font-medium">
                            Nom complet *
                        </label>
                        <input
                            type="text"
                            value={customerInfo.name}
                            onChange={(e) =>
                                setCustomerInfo({ ...customerInfo, name: e.target.value })
                            }
                            placeholder="Ex: Jean Kabongo"
                            className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 dark:text-store-gray mb-1 block font-medium">
                            Téléphone *
                        </label>
                        <input
                            type="tel"
                            value={customerInfo.phone}
                            onChange={(e) =>
                                setCustomerInfo({ ...customerInfo, phone: e.target.value })
                            }
                            placeholder="Ex: +243 97 123 4567"
                            className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs text-slate-500 dark:text-store-gray mb-1 flex items-center justify-between font-medium">
                        <span>Email de réception (Reçu)</span>
                        <span className="text-[10px] text-slate-400 font-normal border border-slate-200 dark:border-store-border px-1.5 rounded bg-slate-50 dark:bg-store-gray-dark">Facultatif</span>
                    </label>
                    <input
                        type="email"
                        value={customerInfo.email || ''}
                        onChange={(e) =>
                            setCustomerInfo({ ...customerInfo, email: e.target.value })
                        }
                        placeholder="Ex: jean@gmail.com"
                        className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                    />
                </div>
            </div>

            {/* City selection */}
            <div className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-4">
                <h2 className="font-bold text-lg flex items-center gap-2">
                    <Icon icon="solar:map-point-bold-duotone" className="text-lime-500 text-xl" />
                    Ville de livraison
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CITIES.map((city) => (
                        <button
                            key={city.id}
                            onClick={() => {
                                setSelectedCity(city.id);
                                setSelectedDelivery(null);
                            }}
                            className={`relative p-3 rounded-xl border text-sm font-semibold transition-all duration-200 text-center ${selectedCity === city.id
                                ? 'bg-lime-500/10 border-lime-500 text-lime-600 dark:text-lime-400 shadow-sm'
                                : 'bg-slate-50 dark:bg-store-gray-dark/50 border-slate-200 dark:border-store-border text-slate-600 dark:text-store-gray hover:border-lime-500/40'
                                }`}
                        >
                            <Icon
                                icon="solar:map-point-bold"
                                className={`text-lg mb-1 mx-auto block ${selectedCity === city.id
                                    ? 'text-lime-500'
                                    : 'text-slate-400 dark:text-store-gray'
                                    }`}
                            />
                            {city.name}
                            {city.id === 'lubumbashi' && (
                                <span className="absolute -top-1.5 -right-1.5 bg-lime-500 text-black text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                                    HQ
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Delivery mode */}
            {cityData && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-4"
                >
                    <h2 className="font-bold text-lg flex items-center gap-2">
                        <Icon icon="solar:delivery-bold-duotone" className="text-lime-500 text-xl" />
                        Mode de livraison — {cityData.name}
                    </h2>
                    <div className="space-y-3">
                        {cityData.deliveryModes.map((mode) => (
                            <button
                                key={mode.id}
                                onClick={() => setSelectedDelivery(mode.id)}
                                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 text-left ${selectedDelivery === mode.id
                                    ? 'bg-lime-500/10 border-lime-500 shadow-sm'
                                    : 'bg-slate-50 dark:bg-store-gray-dark/50 border-slate-200 dark:border-store-border hover:border-lime-500/40'
                                    }`}
                            >
                                <div
                                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${selectedDelivery === mode.id
                                        ? 'bg-lime-500/20 text-lime-500'
                                        : 'bg-slate-200 dark:bg-store-gray-dark text-slate-400 dark:text-store-gray'
                                        }`}
                                >
                                    <Icon icon={mode.icon} className="text-2xl" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-sm text-slate-900 dark:text-white">
                                        {mode.label}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-store-gray">
                                        {mode.transport} · {mode.delay}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span
                                        className={`font-bold text-sm ${mode.price === 0
                                            ? 'text-lime-500'
                                            : 'text-slate-900 dark:text-white'
                                            }`}
                                    >
                                        {mode.price === 0 ? 'Gratuit' : `${mode.price.toFixed(2)} $US`}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* Address field for home delivery */}
            {selectedDelivery === 'domicile' && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-3"
                >
                    <h2 className="font-bold text-lg flex items-center gap-2">
                        <Icon icon="solar:home-bold-duotone" className="text-lime-500 text-xl" />
                        Adresse de livraison
                    </h2>
                    <input
                        type="text"
                        value={customerInfo.address}
                        onChange={(e) =>
                            setCustomerInfo({ ...customerInfo, address: e.target.value })
                        }
                        placeholder="Ex: 123 Avenue Lumumba, Commune Lubumbashi"
                        className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                    />
                </motion.div>
            )}

            {/* Continue button */}
            <motion.button
                onClick={onContinue}
                disabled={!canProceed}
                whileHover={canProceed ? { scale: 1.01 } : {}}
                whileTap={canProceed ? { scale: 0.98 } : {}}
                className={`w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300 group ${canProceed
                    ? 'bg-lime-500 hover:bg-lime-400 text-black shadow-lg shadow-lime-500/20'
                    : 'bg-slate-200 dark:bg-store-gray-dark text-slate-400 dark:text-store-gray cursor-not-allowed'
                    }`}
            >
                Continuer vers le paiement
                <Icon icon="guidance:left-arrow" className='text-xl group-hover:translate-x-2 transition-all duration-300' />
            </motion.button>
        </motion.div>
    );
};

export default StepLivraison;
